import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GameStyles.css';

const DeductiveGame = () => {
  const [level, setLevel] = useState(1);
  const [points, setPoints] = useState(0);
  const [time, setTime] = useState(20);
  const [gameStarted, setGameStarted] = useState(false);
  const [matrix, setMatrix] = useState([]);
  const [options, setOptions] = useState([]);
  const [emptyCell, setEmptyCell] = useState(null); // Only ONE cell with ?
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [gamePaused, setGamePaused] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'correct' or 'wrong'
  const [difficulty, setDifficulty] = useState('medium'); // 'easy', 'medium', 'hard'

  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const shapes = ['square', 'add', 'triangle', 'circle', 'divide', 'minus', 'cross', 'equal'];
  const gridSizes = { small: 3, medium: 4, large: 5 };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleTimerEnd = useCallback(() => {
    alert(`Game Over! Your final score: ${points}`);
    setGameStarted(false);
    setLevel(1);
    setPoints(0);
  }, [points]);

  useEffect(() => {
    if (gameStarted && !gamePaused) {
      timerRef.current = setInterval(() => {
        setTime((prev) => {
          if (prev <= 1) {
            handleTimerEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameStarted, gamePaused, handleTimerEnd]);

  const shuffle = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const createLevel = () => {
    let gridSize, timeLimit, cellsToRemove;

    // Set difficulty based on current difficulty level
    if (difficulty === 'easy') {
      gridSize = gridSizes.small; // 3x3
      timeLimit = 25;
      cellsToRemove = 3; // Remove 3 cells total (including question mark)
    } else if (difficulty === 'medium') {
      gridSize = gridSizes.medium; // 4x4
      timeLimit = 30;
      cellsToRemove = 5; // Remove 5 cells total
    } else {
      gridSize = gridSizes.large; // 5x5
      timeLimit = 35;
      cellsToRemove = 8; // Remove 8 cells total
    }

    const availableShapes = shapes.slice(0, gridSize);
    const shuffledShapes = shuffle(availableShapes);

    // Create a valid matrix where no row/column has repeating symbols
    const newMatrix = [];
    for (let row = 0; row < gridSize; row++) {
      const rowArray = [];
      for (let col = 0; col < gridSize; col++) {
        const shapeIndex = (row + col) % gridSize;
        rowArray.push(shuffledShapes[shapeIndex]);
      }
      newMatrix.push(rowArray);
    }

    // Select ONE random cell to be the question mark (the one user needs to solve)
    const questionRow = Math.floor(Math.random() * gridSize);
    const questionCol = Math.floor(Math.random() * gridSize);
    const answer = newMatrix[questionRow][questionCol];

    // Get all positions except the question mark position
    const allPositions = [];
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        if (i !== questionRow || j !== questionCol) {
          allPositions.push({ row: i, col: j });
        }
      }
    }

    // Shuffle and select cells to remove (but not the question mark cell yet)
    const shuffledPositions = shuffle(allPositions);
    const cellsToEmpty = shuffledPositions.slice(0, cellsToRemove - 1);

    // Remove selected cells (make them transparent/empty but not question marks)
    cellsToEmpty.forEach(pos => {
      newMatrix[pos.row][pos.col] = 'empty'; // Mark as empty (not question mark)
    });

    // Set the question mark cell to null (will show ?)
    newMatrix[questionRow][questionCol] = null;

    setMatrix(newMatrix);
    setEmptyCell({ row: questionRow, col: questionCol });
    setCorrectAnswer(answer);
    setOptions(shuffle(availableShapes));
    setTime(timeLimit);
  };

  const handleStartGame = () => {
    setGameStarted(true);
    setLevel(1);
    setPoints(0);
    setDifficulty('medium'); // Start at medium difficulty
    createLevel();
  };

  const handleOptionClick = (selectedShape) => {
    if (feedback) return; // Prevent multiple clicks during feedback

    const isCorrect = selectedShape === correctAnswer;

    if (isCorrect) {
      // Correct answer!
      setFeedback('correct');
      setPoints(prev => prev + 1);

      // Fill in the cell
      const newMatrix = matrix.map((r, i) =>
        r.map((c, j) => (i === emptyCell.row && j === emptyCell.col ? selectedShape : c))
      );
      setMatrix(newMatrix);

      // Increase difficulty on correct answer
      if (difficulty === 'easy') {
        setDifficulty('medium');
      } else if (difficulty === 'medium') {
        setDifficulty('hard');
      }
      // If already hard, stay hard

      // Show feedback and move to next level
      setTimeout(() => {
        setFeedback(null);
        setLevel(prev => prev + 1);
        createLevel();
      }, 800);
    } else {
      // Wrong answer!
      setFeedback('wrong');
      setPoints(prev => Math.max(0, prev - 1));

      // Decrease difficulty on wrong answer
      if (difficulty === 'hard') {
        setDifficulty('medium');
      } else if (difficulty === 'medium') {
        setDifficulty('easy');
      }
      // If already easy, stay easy

      // Clear feedback after a moment
      setTimeout(() => {
        setFeedback(null);
      }, 800);
    }
  };

  const togglePause = () => {
    setGamePaused(prev => !prev);
  };

  const toggleMusic = () => {
    setMusicEnabled(prev => !prev);
    if (audioRef.current) {
      if (musicEnabled) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  return (
    <div className="game-container deductive-game">
      <audio ref={audioRef} loop>
        <source src="/assets/deductive/audio/audio.mp3" type="audio/mpeg" />
      </audio>

      {!gameStarted ? (
        <div className="rules-container">
          <h1 className="game-title">Deductive Challenge</h1>
          <div className="rules-content">
            <p>
              A grid with symbols is displayed where one cell has a question mark (?).
              Use your deductive reasoning to figure out which symbol belongs in the empty cell
              by analyzing the row and column patterns. Remember: no symbol repeats in any row or column!
            </p>
            <h3>How to Play</h3>
            <ul>
              <li>Look at the symbols in the same row and column as the question mark</li>
              <li>Deduce which symbol is missing based on the pattern</li>
              <li>Click the correct symbol from the options below</li>
              <li>Correct answer: +1 point and difficulty increases</li>
              <li>Wrong answer: -1 point and difficulty decreases</li>
              <li>Difficulty levels: ⭐ Easy (3x3), ⭐⭐ Medium (4x4), ⭐⭐⭐ Hard (5x5)</li>
            </ul>
            <button className="play-button" onClick={handleStartGame}>
              Play
            </button>
          </div>
        </div>
      ) : (
        <div className="game-play-area">
          <div className="game-header">
            <div className="control-buttons">
              <button onClick={togglePause} className="control-btn">
                {gamePaused ? '▶️' : '⏸️'}
              </button>
              <button onClick={toggleMusic} className="control-btn">
                {musicEnabled ? '🔊' : '🔇'}
              </button>
            </div>
            <div className="game-stat">Level {level}</div>
            <div className="game-stat">Points: {points}</div>
            <div className="game-stat">Time: {time}s</div>
            <div className="game-stat">
              {difficulty === 'easy' ? '⭐ Easy' : difficulty === 'medium' ? '⭐⭐ Medium' : '⭐⭐⭐ Hard'}
            </div>
            <div className="game-name">Deductive Game</div>
          </div>

          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(time / 30) * 100}%` }}></div>
          </div>

          <div className="game-content">
            {/* Feedback Animation */}
            {feedback && (
              <div className={`feedback-animation ${feedback}`}>
                {feedback === 'correct' ? (
                  <>
                    <img src="/assets/deductive/images/correct.png" alt="Correct!" className="feedback-icon" />
                    <span className="feedback-text">Correct! +1</span>
                  </>
                ) : (
                  <>
                    <img src="/assets/deductive/images/wrong.png" alt="Wrong!" className="feedback-icon" />
                    <span className="feedback-text">Wrong! -1</span>
                  </>
                )}
              </div>
            )}

            <div className="grid-container" style={{ gridTemplateColumns: `repeat(${matrix.length}, 1fr)` }}>
              {matrix.map((row, rowIndex) =>
                row.map((cell, colIndex) => (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={`grid-cell ${cell === null ? 'question-cell' : cell === 'empty' ? 'removed-cell' : ''}`}
                  >
                    {cell === null ? (
                      <img src={`/assets/deductive/images/toFind.png`} alt="?" className="question-mark" />
                    ) : cell === 'empty' ? (
                      <img src={`/assets/deductive/images/transparent.png`} alt="" className="transparent-img" />
                    ) : (
                      <img src={`/assets/deductive/images/${cell}.png`} alt={cell} />
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="options-container">
              {options.map((shape, index) => (
                <div
                  key={index}
                  className={`option-cell ${feedback ? 'disabled' : ''}`}
                  onClick={() => handleOptionClick(shape)}
                >
                  <img src={`/assets/deductive/images/${shape}.png`} alt={shape} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeductiveGame;
